import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Container, Paper, Chip, CircularProgress,
} from '@mui/material';
import {
  MovieFilter as DCPIcon,
  Shield as ShieldIcon,
  AdminPanelSettings as OperatorIcon,
  ChevronRight as ChevronIcon,
} from '@mui/icons-material';
import { fetchMyCompanies, setActiveCompany } from '../store/slices/companySlice';

const GOLD = '#d4a017';
const BG_DARK = '#0a0c10';
const BG_CARD = '#12151c';
const TEXT_PRIMARY = '#e8e6e3';
const TEXT_SECONDARY = 'rgba(232,230,227,0.55)';
const BORDER = 'rgba(212,160,23,0.12)';

function CompanyPicker() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading } = useSelector((s) => s.company);
  const { userInfo } = useSelector((s) => s.auth);

  useEffect(() => {
    dispatch(fetchMyCompanies());
  }, [dispatch]);

  // v10.19.9: campaigns is the primary landing surface post-login.
  // For users with one company we auto-pick; either way the next
  // route is /campaigns, not the system dashboard.
  //
  // v10.19.29: clear any stale activeCampaignId from a previous
  // session so the campaigns-gate engages cleanly. Without this, a
  // superadmin who switched workspaces would still see the full
  // sidebar because the old company's campaign id was still in
  // localStorage and the gate read it as "picked."
  const _clearCampaign = () => {
    try { localStorage.removeItem('activeCampaignId'); } catch (_e) { /* */ }
  };
  useEffect(() => {
    if (!loading && list.length === 1 && !userInfo?.is_superadmin) {
      _clearCampaign();
      dispatch(setActiveCompany(list[0].id));
      navigate('/campaigns', { replace: true });
    }
  }, [loading, list, userInfo, dispatch, navigate]);

  const handlePick = (companyId) => {
    _clearCampaign();
    dispatch(setActiveCompany(companyId));
    navigate('/campaigns', { replace: true });
  };

  const handleOperator = () => {
    dispatch(setActiveCompany(null));
    navigate('/operator', { replace: true });
  };

  return (
    <Box sx={{ bgcolor: BG_DARK, color: TEXT_PRIMARY, minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 1 }}>
            Welcome back{userInfo?.full_name ? `, ${userInfo.full_name.split(' ')[0]}` : ''}
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY }}>
            Choose a workspace to continue
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        ) : (
          <>
            {userInfo?.is_superadmin && (
              <Paper
                onClick={handleOperator}
                sx={{
                  p: 3, mb: 2, bgcolor: BG_CARD, border: `1px solid ${GOLD}`, borderRadius: 2,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2,
                  '&:hover': { bgcolor: 'rgba(212,160,23,0.06)' },
                }}
              >
                <OperatorIcon sx={{ color: GOLD, fontSize: 32 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>HHMG Operator Panel</Typography>
                  <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                    Manage companies, subscriptions, and infrastructure
                  </Typography>
                </Box>
                <ChevronIcon sx={{ color: GOLD }} />
              </Paper>
            )}

            {list.map((company) => (
              <Paper
                key={company.id}
                onClick={() => handlePick(company.id)}
                sx={{
                  p: 3, mb: 1.5, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 2,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2,
                  '&:hover': { borderColor: GOLD, bgcolor: 'rgba(212,160,23,0.04)' },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>{company.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {company.has_press_review && (
                      <Chip
                        icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                        label="Watermarks"
                        size="small"
                        sx={{ bgcolor: 'rgba(212,160,23,0.1)', color: TEXT_PRIMARY, fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {company.has_theatrical_dcp && (
                      <Chip
                        icon={<DCPIcon sx={{ fontSize: 14 }} />}
                        label="Theatrical DCP"
                        size="small"
                        sx={{ bgcolor: 'rgba(212,160,23,0.1)', color: TEXT_PRIMARY, fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    <Chip
                      label={company.role}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: BORDER, color: TEXT_SECONDARY, fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                </Box>
                <ChevronIcon sx={{ color: TEXT_SECONDARY }} />
              </Paper>
            ))}

            {list.length === 0 && !userInfo?.is_superadmin && (
              <Paper sx={{ p: 4, bgcolor: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 1 }}>No workspaces yet</Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                  You don't belong to any production company yet. Contact your administrator.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default CompanyPicker;
